/**
 * `/files/:libraryKey` and `/files/:libraryKey/:folderId` — the file browser.
 *
 * The screen the whole resource model exists for, and the one place the
 * capability rule earns its keep: **every button here is rendered from the
 * `capabilities` the server attached to the resource it acts on.** Nothing
 * computes access. A row whose `capabilities.delete` is false has no delete item
 * in its menu, and a caller who cannot read a resource never saw the row at all
 * — the server answers 404 rather than 403, so there is nothing to grey out.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HistoryIcon from '@mui/icons-material/History';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ShareIcon from '@mui/icons-material/Share';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ResourceDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { SortableHeadCell } from '../../components/SortableHeadCell';
import { TablePager } from '../../components/TablePager';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { SubjectPicker } from '../../components/SubjectPicker';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useSubmit } from '../../hooks/useSubmit';
import { searchSubjects } from '../../api/identity';
import {
  contentUrl,
  createFolder,
  getLibraryRoot,
  getResource,
  listChildren,
  transferOwnership,
  trashResource,
  updateResource,
  uploadFile,
} from '../../api/resources';
import { MoveDialog } from './MoveDialog';
import { ShareDialog } from './ShareDialog';
import { VersionsDialog } from './VersionsDialog';

/**
 * The chain from the library root down to `resource`.
 *
 * One request per level, walked upward. That is not as wasteful as it looks —
 * `depth` is on the resource, folder trees are shallow, and the alternative is
 * an endpoint that returns ancestors, which does not exist. If trees get deep
 * enough for this to matter, the fix is that endpoint, not caching it here.
 */
async function loadAncestors(resource: ResourceDto): Promise<ResourceDto[]> {
  const chain: ResourceDto[] = [];
  let parentId = resource.parentId;

  while (parentId) {
    const { resource: parent } = await getResource(parentId);
    chain.unshift(parent);
    parentId = parent.parentId;
  }

  return chain;
}

function KindIcon({ kind }: { kind: ResourceDto['kind'] }) {
  return kind === 'folder' ? (
    <FolderIcon fontSize="small" sx={{ color: 'primary.main' }} />
  ) : (
    <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
  );
}

export default function BrowsePage() {
  const { t } = useTranslation('files');
  const { libraryKey = '', folderId } = useParams();
  const navigate = useNavigate();

  // No folder in the URL means the library root, which the server creates on
  // first access. Keyed on both so switching library re-resolves.
  const current = useAsync(
    async () =>
      folderId ? (await getResource(folderId)).resource : (await getLibraryRoot(libraryKey)).resource,
    [libraryKey, folderId],
  );

  const folder = current.data;

  // `kind` is the default and means folders first, then files, each by name —
  // the arrangement a file browser reads as "unsorted". Navigating to another
  // folder resets the page; page three of one folder means nothing in the next.
  const list = useListQuery({ defaultSort: 'kind', defaultLimit: 50, resetKey: folder?.id });

  const children = useAsync(
    () => listChildren(folder?.id ?? '', list.query),
    [folder?.id, list.key],
    { enabled: folder !== undefined },
  );

  const ancestors = useAsync(() => (folder ? loadAncestors(folder) : Promise.resolve([])), [
    folder?.id,
  ]);

  // --- dialogs --------------------------------------------------------------

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [menuFor, setMenuFor] = useState<{ anchor: HTMLElement; resource: ResourceDto } | null>(null);
  const [renaming, setRenaming] = useState<ResourceDto | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [sharing, setSharing] = useState<ResourceDto | null>(null);
  const [versioning, setVersioning] = useState<ResourceDto | null>(null);
  const [moving, setMoving] = useState<ResourceDto | null>(null);
  const [transferring, setTransferring] = useState<ResourceDto | null>(null);
  const [deleting, setDeleting] = useState<ResourceDto | null>(null);

  const [newOwner, setNewOwner] = useState<{ id: number; displayName: string } | null>(null);
  const owners = useSubjectSearch((query) => searchSubjects(query, { type: 'user' }), {
    enabled: transferring !== null,
  });

  // --- uploads --------------------------------------------------------------

  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<{ name: string; progress: number } | null>(null);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [dragging, setDragging] = useState(false);

  const runUploads = useCallback(
    async (files: FileList | File[]) => {
      if (!folder) return;
      setUploadError(null);

      // Sequential, not parallel. Ten concurrent streaming uploads is ten open
      // write handles on the server for no benefit to the user, who can only
      // watch one progress bar anyway.
      for (const file of Array.from(files)) {
        setUploading({ name: file.name, progress: 0 });
        try {
          await uploadFile(folder.id, file, {
            onProgress: (fraction) => setUploading({ name: file.name, progress: fraction }),
          });
        } catch (error) {
          setUploadError(error instanceof Error ? error : new Error(String(error)));
          break;
        }
      }

      setUploading(null);
      children.reload();
    },
    [folder, children],
  );

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    if (!folder?.capabilities.write) return;
    if (event.dataTransfer.files.length > 0) void runUploads(event.dataTransfer.files);
  }

  // --- mutations ------------------------------------------------------------

  const create = useSubmit(async () => {
    if (!folder) return undefined;
    const created = await createFolder(folder.id, { name: newFolderName.trim() });
    setNewFolderName('');
    children.reload();
    return created;
  });

  const rename = useSubmit(async () => {
    if (!renaming) return undefined;
    const renamed = await updateResource(renaming.id, { name: renameTo.trim() });
    // Renaming the folder you are standing in changes the header, not the list.
    if (renaming.id === folder?.id) current.reload();
    else children.reload();
    return renamed;
  });

  const trash = useSubmit(async () => {
    if (!deleting) return undefined;
    const result = await trashResource(deleting.id);
    setDeleting(null);
    // Deleting the folder you are in leaves nowhere to stand: go up.
    if (deleting.id === folder?.id) {
      const parent = ancestors.data?.at(-1);
      navigate(parent ? `/files/${libraryKey}/${parent.id}` : `/files/${libraryKey}`);
    } else {
      children.reload();
    }
    return result;
  });

  const transfer = useSubmit(async () => {
    if (!transferring || !newOwner) return undefined;
    const result = await transferOwnership(transferring.id, { ownerId: newOwner.id });
    setTransferring(null);
    setNewOwner(null);
    owners.reset();
    if (transferring.id === folder?.id) current.reload();
    else children.reload();
    return result;
  });

  const mayWrite = folder?.capabilities.write ?? false;

  return (
    <>
      <AsyncContent state={current}>
        {(resource) => (
          <>
            <PageHeader
              above={
                <Breadcrumbs sx={{ fontSize: 14 }}>
                  <Link component={RouterLink} to="/files" underline="hover" color="inherit">
                    {t('browse.breadcrumbRoot')}
                  </Link>
                  {(ancestors.data ?? []).map((ancestor) => (
                    <Link
                      key={ancestor.id}
                      component={RouterLink}
                      to={`/files/${libraryKey}/${ancestor.id}`}
                      underline="hover"
                      color="inherit"
                    >
                      {ancestor.name}
                    </Link>
                  ))}
                  <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                    {resource.name}
                  </Typography>
                </Breadcrumbs>
              }
              title={
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{resource.name}</span>
                  {!resource.inheritsGrants && (
                    <Tooltip title={t('browse.notInheritingTooltip')}>
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<LinkOffIcon />}
                        label={t('browse.notInheriting')}
                      />
                    </Tooltip>
                  )}
                </Stack>
              }
              description={t('browse.inLibrary', { library: resource.library })}
              actions={
                <>
                  {resource.capabilities.share && (
                    <Button
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={() => setSharing(resource)}
                    >
                      {t('browse.share')}
                    </Button>
                  )}
                  {mayWrite && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CreateNewFolderIcon />}
                        onClick={() => setNewFolderOpen(true)}
                      >
                        {t('browse.newFolder')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => inputRef.current?.click()}
                      >
                        {t('browse.upload')}
                      </Button>
                    </>
                  )}
                </>
              }
            />

            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                if (event.target.files) void runUploads(event.target.files);
                // Cleared so re-picking the same file fires change again.
                event.target.value = '';
              }}
            />

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('browse.uploading', {
                    name: uploading.name,
                    percent: Math.round(uploading.progress * 100),
                  })}
                </Typography>
                <LinearProgress variant="determinate" value={uploading.progress * 100} />
              </Box>
            )}
            {uploadError && <ErrorState error={uploadError} />}

            <Paper
              variant="outlined"
              onDragOver={(event) => {
                event.preventDefault();
                if (mayWrite) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              sx={{
                overflow: 'hidden',
                outline: dragging ? 2 : 0,
                outlineStyle: 'dashed',
                outlineColor: 'primary.main',
                transition: 'outline-width 120ms',
              }}
            >
              <AsyncContent state={children}>
                {({ resources, page }) =>
                  resources.length === 0 ? (
                    <EmptyState
                      icon={<FolderOpenIcon fontSize="inherit" />}
                      title={t('browse.emptyTitle')}
                      description={
                        mayWrite ? t('browse.emptyWritable') : t('browse.emptyReadOnly')
                      }
                    />
                  ) : (
                    <>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <SortableHeadCell
                            field="name"
                            activeField={list.sort}
                            activeOrder={list.order}
                            onSort={list.toggleSort}
                          >
                            {t('browse.column.name')}
                          </SortableHeadCell>
                          {/* Owner is not sortable either, for a duller reason
                              than access: the name lives in `users`, not on the
                              resource row, so ordering by it needs a join and a
                              normalised key column to sort on. Add both before
                              adding the header. */}
                          <TableCell sx={{ width: 160 }}>{t('browse.column.owner')}</TableCell>
                          <SortableHeadCell
                            field="updatedAt"
                            activeField={list.sort}
                            activeOrder={list.order}
                            onSort={list.toggleSort}
                            sx={{ width: 140 }}
                          >
                            {t('browse.column.changed')}
                          </SortableHeadCell>
                          {/* Access is not a column. It is resolved per caller
                              from grants on this resource and every folder above
                              it, which no `ORDER BY` can express — see the
                              backend's resources/access.ts. */}
                          <TableCell sx={{ width: 120 }}>{t('browse.column.access')}</TableCell>
                          <TableCell sx={{ width: 56 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resources.map((child) => (
                          <TableRow key={child.id} hover>
                            <TableCell>
                              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                <KindIcon kind={child.kind} />
                                {child.kind === 'folder' ? (
                                  <Link
                                    component={RouterLink}
                                    to={`/files/${libraryKey}/${child.id}`}
                                    underline="hover"
                                    color="inherit"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {child.name}
                                  </Link>
                                ) : (
                                  <Typography variant="body2">{child.name}</Typography>
                                )}
                                {!child.inheritsGrants && child.kind === 'folder' && (
                                  <Tooltip title={t('browse.notInheritingRow')}>
                                    <LinkOffIcon fontSize="small" color="warning" />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {child.ownerName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                <Timestamp value={child.updatedAt} />
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* Straight from the payload — the interface never
                                  works this out, so it cannot disagree. */}
                              <Stack direction="row" spacing={0.5}>
                                {child.capabilities.write && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('browse.access.write')}
                                  />
                                )}
                                {child.capabilities.share && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('browse.access.share')}
                                  />
                                )}
                                {!child.capabilities.write && !child.capabilities.share && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('browse.access.read')}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                aria-label={t('browse.rowActions', { name: child.name })}
                                onClick={(event) =>
                                  setMenuFor({ anchor: event.currentTarget, resource: child })
                                }
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {/* `page.total` is null for a folder — children the caller
                        cannot read are dropped after the query — so this renders
                        prev/next without a page count. TablePager handles it. */}
                    <TablePager
                      page={page}
                      current={list.page}
                      onPageChange={list.setPage}
                      onLimitChange={list.setLimit}
                    />
                    </>
                  )
                }
              </AsyncContent>
            </Paper>

            {/* --- the per-row menu, built from that row's capabilities --- */}
            <Menu
              anchorEl={menuFor?.anchor ?? null}
              open={menuFor !== null}
              onClose={() => setMenuFor(null)}
            >
              {menuFor?.resource.kind === 'file' && (
                <MenuItem
                  component="a"
                  href={contentUrl(menuFor.resource.id)}
                  onClick={() => setMenuFor(null)}
                >
                  <ListItemIcon>
                    <DownloadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('browse.menu.download')}</ListItemText>
                </MenuItem>
              )}

              {menuFor?.resource.kind === 'file' && (
                <MenuItem
                  onClick={() => {
                    setVersioning(menuFor.resource);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <HistoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('browse.menu.versions')}</ListItemText>
                </MenuItem>
              )}

              {menuFor?.resource.capabilities.write && (
                <MenuItem
                  onClick={() => {
                    setRenaming(menuFor.resource);
                    setRenameTo(menuFor.resource.name);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('browse.menu.rename')}</ListItemText>
                </MenuItem>
              )}

              {menuFor?.resource.capabilities.write && (
                <MenuItem
                  onClick={() => {
                    setMoving(menuFor.resource);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <DriveFileMoveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('browse.menu.move')}</ListItemText>
                </MenuItem>
              )}

              {menuFor && (
                <MenuItem
                  onClick={() => {
                    setSharing(menuFor.resource);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <ShareIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {menuFor.resource.capabilities.share
                      ? t('browse.menu.share')
                      : t('browse.menu.whoHasAccess')}
                  </ListItemText>
                </MenuItem>
              )}

              {menuFor?.resource.capabilities.share && (
                <MenuItem
                  onClick={() => {
                    setTransferring(menuFor.resource);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <PersonAddAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('browse.menu.transfer')}</ListItemText>
                </MenuItem>
              )}

              {menuFor?.resource.capabilities.delete && [
                <Divider key="divider" />,
                <MenuItem
                  key="delete"
                  onClick={() => {
                    setDeleting(menuFor.resource);
                    setMenuFor(null);
                  }}
                >
                  <ListItemIcon>
                    <DeleteOutlinedIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>
                    {t('browse.menu.trash')}
                  </ListItemText>
                </MenuItem>,
              ]}
            </Menu>

            {/* --- dialogs --- */}

            <FormDialog
              open={newFolderOpen}
              title={t('browse.newFolderDialog.title')}
              submitLabel={t('browse.newFolderDialog.submit')}
              maxWidth="xs"
              pending={create.pending}
              error={create.error}
              disabled={newFolderName.trim() === ''}
              onClose={() => setNewFolderOpen(false)}
              onSubmit={async () => {
                if (await create.submit()) setNewFolderOpen(false);
              }}
            >
              <TextField
                label={t('browse.newFolderDialog.name')}
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                autoFocus
                fullWidth
              />
            </FormDialog>

            <FormDialog
              open={renaming !== null}
              title={t('browse.renameDialog.title', { name: renaming?.name ?? '' })}
              submitLabel={t('browse.renameDialog.submit')}
              maxWidth="xs"
              pending={rename.pending}
              error={rename.error}
              disabled={renameTo.trim() === '' || renameTo === renaming?.name}
              onClose={() => setRenaming(null)}
              onSubmit={async () => {
                if (await rename.submit()) setRenaming(null);
              }}
            >
              <TextField
                label={t('browse.renameDialog.name')}
                value={renameTo}
                onChange={(event) => setRenameTo(event.target.value)}
                autoFocus
                fullWidth
              />
            </FormDialog>

            <FormDialog
              open={transferring !== null}
              title={t('browse.transferDialog.title')}
              submitLabel={t('browse.transferDialog.submit')}
              pending={transfer.pending}
              error={transfer.error}
              disabled={newOwner === null}
              onClose={() => setTransferring(null)}
              onSubmit={() => void transfer.submit()}
            >
              <Alert severity="warning">
                <Trans
                  i18nKey="browse.transferDialog.warning"
                  ns="files"
                  values={{ name: transferring?.name ?? '' }}
                  components={{ strong: <strong /> }}
                />
              </Alert>
              <SubjectPicker
                options={owners.options}
                value={
                  newOwner ? { type: 'user', id: newOwner.id, displayName: newOwner.displayName } : null
                }
                loading={owners.loading}
                label={t('browse.transferDialog.newOwner')}
                onChange={(subject) =>
                  setNewOwner(subject ? { id: subject.id, displayName: subject.displayName } : null)
                }
                onQueryChange={owners.setQuery}
              />
            </FormDialog>

            {/* No confirmation for the trash — the trash *is* the confirmation,
                and one on a reversible act teaches people to click through them. */}
            <ConfirmDialog
              open={deleting !== null}
              title={t('browse.trashDialog.title')}
              tone="error"
              confirmLabel={t('browse.trashDialog.confirm')}
              pending={trash.pending}
              error={trash.error}
              onClose={() => setDeleting(null)}
              onConfirm={() => void trash.submit()}
              message={
                deleting?.kind === 'folder'
                  ? t('browse.trashDialog.folder', { name: deleting.name })
                  : t('browse.trashDialog.file', { name: deleting?.name ?? '' })
              }
            />

            {sharing && (
              <ShareDialog
                resource={sharing}
                open
                onClose={() => setSharing(null)}
                onChanged={() => {
                  if (sharing.id === resource.id) current.reload();
                  else children.reload();
                }}
              />
            )}

            {versioning && (
              <VersionsDialog
                resource={versioning}
                open
                onClose={() => setVersioning(null)}
                onChanged={() => children.reload()}
              />
            )}

            {moving && (
              <MoveDialog
                resource={moving}
                open
                onClose={() => setMoving(null)}
                onMoved={() => children.reload()}
              />
            )}
          </>
        )}
      </AsyncContent>
    </>
  );
}
